import React, { createRef } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import "./PopUpAlbums.scss";
import { Album, AlbumDetail } from "../../../model/domain/album";
import { MediaFile } from "../../common/model/media";
import TaskAlbums from "../../../ws/TaskAlbums";
import { Media } from "../../../model/domain/media";
import { User } from "../../../model/domain/user";
import { LoadingView } from "@cuatroochenta/co-admin-react-library";
import { translate } from "../../../../I18n/i18n";
import PopUpRemove from "../../common/components/PopUpRemove/PopUpRemove";
import { ShareCenterOption } from "../../../model/domain/share-option";
import { ShareOptionsUtils } from "../../../../utils/map-utils";
import AdvancedShareWithCentersSelector from "../../common/components/AdvancedShareWithCentersSelector/AdvancedShareWithCentersSelector";
import AdvancedShareWithCentersToolbar from "../../common/components/AdvancedShareWithCentersToolbar/AdvancedShareWithCentersToolbar";

interface PopUpAlbumsProps extends WithTranslation {
    show: boolean
    onClose: (album?: Album) => void
    onSubmit: (data: { 
        title: string,
        media: File[],
        isForAllCenters: string,
        isCreate: boolean,
        shareOptions: ShareCenterOption[]
    }) => void,
    album?: AlbumDetail
    task: TaskAlbums
    forwardedRef?: React.Ref<PopUpAlbums>
    user: User
}

interface PopUpAlbumsState {
    title: string
    media: File[]
    isForAllCenters: boolean
    album?: AlbumDetail
    isLoadingMedia: boolean
    isSubmitting: boolean
    showPopupRemove: boolean
    mediaIndexToRemove: number
    newMedia: File[]
    isNewMedia: boolean
    combinedMedia: MediaFile[]
    showPublishAll: boolean
    errorMessage: string
    loadingShareOptions: boolean;
    shareOptions?: ShareCenterOption[]
    selectedNames: string[]
}

class PopUpAlbums extends React.Component<PopUpAlbumsProps, PopUpAlbumsState> {
    private fileInputRef = createRef<HTMLInputElement>();

    constructor(props: PopUpAlbumsProps) {
        super(props);
        this.fileInputRef = React.createRef();
        this.state = {
            title: this.props.album?.title || '',
            media: [],
            isForAllCenters: false,
            album: this.props.album,
            isLoadingMedia: false,
            isSubmitting: false,
            showPopupRemove: false,
            mediaIndexToRemove: -1,
            newMedia: [],
            isNewMedia: false,
            combinedMedia: [],
            showPublishAll: false,
            errorMessage: '',
            loadingShareOptions: false,
            selectedNames: [],
            shareOptions: this.props.album?.shareOptions
        };
    }

    componentDidMount() {
        if (this.props.show) {
            document.body.classList.add('no-scroll');
        }
    }

    componentWillUnmount() {
        document.body.classList.remove('no-scroll');
    }

    componentDidUpdate(prevProps: Readonly<PopUpAlbumsProps>, prevState: Readonly<PopUpAlbumsState>, snapshot?: any): void {
        if (this.props.show !== prevProps.show) {
            this.resetState();
            if (this.props.show) {
                document.body.classList.add('no-scroll');
                if (!this.props.album) {
                    this.loadShareOptions();
                }
            } else {
                document.body.classList.remove('no-scroll');
            }
        }

        if (this.props.album !== prevProps.album) {
            this.setState({
                title: this.props.album?.title || '',
                album: this.props.album,
                isForAllCenters: false,
                media: [],
                isLoadingMedia: !!this.props.album?.media,
                shareOptions: this.props.album?.shareOptions
            });

            if (this.props.album?.media) {
                this.loadMedia(this.props.album.media);
            }
        }

        if (prevState.media !== this.state.media || prevState.newMedia !== this.state.newMedia) {
            const combinedMedia = [
                ...(Array.isArray(this.state.media) ? this.state.media.map(file => ({ file, isNewMedia: false })) : []),
                ...(Array.isArray(this.state.newMedia) ? this.state.newMedia.map(file => ({ file, isNewMedia: true })) : [])
            ];

            this.setState({ combinedMedia });
        }

        if (prevState.shareOptions !== this.state.shareOptions) {
            const selectedNames = ShareOptionsUtils.getSelectedNames(this.state.shareOptions)
            this.setState({ selectedNames })
        }
    }

    private loadMedia = async (media: Media[]) => {
        const mediaFiles: File[] = [];

        for (const { id } of media) {
            try {
                let blob: Blob | null = null;

                blob = await this.props.task.fetchMediaById(id.toString(), 'images');

                if (blob) {
                    const file = new File([blob], new Date().getTime().toString(), { type: 'image/jpeg' });
                    mediaFiles.push(file);
                }
            } catch (error) {
                console.error('Error al cargar el medio', error);
            }
        }

        this.setState({ media: mediaFiles, isLoadingMedia: false });
    };

    private loadShareOptions = async () => {
        try {
            this.setState({ loadingShareOptions: true });
            const shareOptions = await this.props.task.getShareOptions();
            this.setState({
                shareOptions,
                loadingShareOptions: false
            });
        } catch (error) {
            console.error(`Error getting album share options: ${error}`);
        }
    };

    private handleSubmit = () => {
        const { title, isForAllCenters, album, newMedia, shareOptions } = this.state;
        const isCreate = !album;

        if (!title) {
            return alert(translate(this.props.t, "albums.TR_ALBUM_TITLE_REQUIRED"));
        }

        this.setState({ isSubmitting: true });

        const mediaArray = Array.isArray(newMedia) ? newMedia : [newMedia];
        const isForAllCentersString = (isForAllCenters === true) ? 'true' : 'false';

        this.props.onSubmit({
            title,
            media: mediaArray,
            isForAllCenters: isForAllCentersString,
            isCreate,
            shareOptions: shareOptions ?? []
        });
    }

    private handleImagePicker = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const fileArray = Array.from(files);

            const imageTypes = ['image/jpeg', 'image/png'];

            const images = fileArray.filter(file => imageTypes.includes(file.type));

            this.setState(prevState => ({
                newMedia: Array.isArray(prevState.newMedia)
                    ? [...prevState.newMedia, ...images]
                    : [...images]
            }));

            if (this.fileInputRef.current) {
                this.fileInputRef.current.value = '';
            }
        }
    };

    handleRemoveMedia = () => {
        const { album, media, mediaIndexToRemove, newMedia, isNewMedia } = this.state;

        if (!Array.isArray(media)) {
            console.error('El tipo de "media" no es un arreglo.');
            return;
        }

        if (isNewMedia) {
            if (Array.isArray(newMedia) && mediaIndexToRemove < newMedia.length) {
                const updatedMedia = [...newMedia];
                updatedMedia.splice(mediaIndexToRemove, 1);
                this.setState({ newMedia: updatedMedia, showPopupRemove: false });
                return;
            }
        }

        if (mediaIndexToRemove < 0 || mediaIndexToRemove >= media.length) {
            console.error('Índice fuera de rango.');
            return;
        }

        const updatedMedia = [...media];
        updatedMedia.splice(mediaIndexToRemove, 1);

        if (album) {
            if (!album || !album.media || !Array.isArray(album.media)) {
                this.setState({ media: updatedMedia, showPopupRemove: false });
                return;
            }

            if (!album.id) {
                console.error('El post no tiene un ID válido.');
                return;
            }

            if (album?.media?.[mediaIndexToRemove]) {
                this.props.task.removeMedia(
                    album.id,
                    album.media[mediaIndexToRemove].id,
                )
                    .then(() => {
                        const updatedMedia = [...media];
                        updatedMedia.splice(mediaIndexToRemove, 1);
                        // Actualizar el post en el estado para reflejar el cambio en los medios
                        const updatedAlbum = {
                            ...album,
                            media: album.media.filter(m => m.id !== mediaIndexToRemove)
                        };

                        this.setState({
                            media: updatedMedia,
                            album: updatedAlbum,
                            showPopupRemove: false
                        });
                    })
                    .catch((error: any) => {
                        console.error('Error al eliminar el medio:', error);
                    });
            } else {
                const updatedMedia = [...media];
                updatedMedia.splice(mediaIndexToRemove, 1);
                this.setState({
                    media: updatedMedia,
                    showPopupRemove: false
                });
            }

        } else {
            const updatedMedia = [...media];
            updatedMedia.splice(mediaIndexToRemove, 1);
            this.setState({ media: updatedMedia });
        }
    };

    private handleAddImageClick = () => {
        this.fileInputRef.current?.click();
    };

    private togglePopupRemove = (index: number, isNewMedia: boolean) => {
        this.setState({
            showPopupRemove: !this.state.showPopupRemove,
            mediaIndexToRemove: index,
            isNewMedia: isNewMedia,
        });
    };

    private renderMediaPreview = () => {
        const { isLoadingMedia, combinedMedia } = this.state;

        if (!combinedMedia || combinedMedia.length === 0) {
            return null;
        }

        if (isLoadingMedia) {
            return <LoadingView loading={true} />;
        }

        // Comprobamos si hay medios para renderizar
        return (
            <div className="media-container">
                {/* Renderizamos los medios de 'media' */}
                {combinedMedia.length > 0 && (
                    <div className="media-section">
                        <div className={`media-preview-container ${combinedMedia.length === 1 ? 'one-image' : combinedMedia.length === 2 ? 'two-images' : combinedMedia.length === 3 ? 'three-images' : ''}`}>
                            {combinedMedia.map((mediaFile, index) => {
                                if (!mediaFile) return null;
                                const mediaLength = Array.isArray(this.state.media) ? this.state.media.length : 0;
                                let originalIndex = index;
                                if (index >= mediaLength) {
                                    originalIndex = index - mediaLength;
                                }
                                const mediaSource = mediaFile.file
                                    ? URL.createObjectURL(mediaFile.file)
                                    : mediaFile.url

                                return (
                                    <div key={index} className="media-preview">
                                        <button
                                            className="remove-button"
                                            onClick={() => this.togglePopupRemove(originalIndex, mediaFile.isNewMedia)}
                                        >
                                            &times;
                                        </button>
                                        <img src={mediaSource} alt={`preview-${index}`} className="media-file" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    resetState = () => {
        this.setState({
            title: this.props.album?.title || '',
            media: [],
            isForAllCenters: false,
            album: this.props.album,
            isLoadingMedia: false,
            isSubmitting: false,
            showPopupRemove: false,
            mediaIndexToRemove: -1,
            newMedia: [],
            isNewMedia: false,
            combinedMedia: [],
            showPublishAll: false,
            errorMessage: '',
            selectedNames: []
        });
    }

    private handleAccept = () => {
        this.setState({ showPublishAll: false })
    };

    private handleCancel = () => {
        this.setState({
            showPublishAll: false,
            selectedNames: [],
            shareOptions: this.state.shareOptions?.map(
                center => ShareOptionsUtils.copyCenterRecursive(center, { checked: false })
            )
        })
    };

    private handleCenterSelection = (selection?: ShareCenterOption[]) => {
        this.setState({ shareOptions: selection })
    };

    private renderPublishAll() {
        const { shareOptions, loadingShareOptions } = this.state;

        return (
            <AdvancedShareWithCentersSelector
                loading={loadingShareOptions}
                shareOptions={shareOptions}
                onAccept={this.handleAccept}
                onCancel={this.handleCancel}
                onSelection={this.handleCenterSelection}
            />
        )
    }

    private renderPublishAlbums() {
        const { t, album } = this.props;
        const { title, isSubmitting, isLoadingMedia, selectedNames, loadingShareOptions } = this.state;

        return (
            <div>
                <div className='popupAlbums-row-1'>
                    <textarea
                        id="content"
                        className='popupAlbums-text-content'
                        placeholder={translate(t, 'albums.TR_TITLE_PLACEHOLDER')}
                        name="content"
                        value={title}
                        onChange={(e) => {
                            this.setState({ title: e.target.value });
                        }}
                        rows={5}
                    />
                </div>

                {isSubmitting || isLoadingMedia ? (
                    <LoadingView loading={true} />
                ) : (
                    <></>
                )}

                <div className="popupAlbums-data">
                    <div className='popupAlbums-media-button-container' style={{ display: 'none' }}>
                        <div className="file-upload">
                            <input
                                type="file"
                                id="image-media"
                                name="media"
                                accept="image/jpeg, image/png"
                                multiple
                                onChange={this.handleImagePicker}
                                className="file-input"
                                ref={this.fileInputRef}
                            />
                            <label htmlFor="image-media" className="file-label" style={{ backgroundImage: 'url(/icons/images-icon.svg)' }}></label>
                        </div>
                    </div>
                    {this.renderMessageCard()}
                    {this.renderMediaPreview()}

                    <button className="btn add-images" onClick={this.handleAddImageClick}>
                        {translate(t, 'albums.TR_ADD_IMAGE_LABEL')}
                    </button>
                </div>
                <div className="divider"></div>
                <div className="popupAlbums-submit" style={{ justifyContent: 'space-between' }}>
                    <AdvancedShareWithCentersToolbar
                        selectedOptions={selectedNames.join(', ')}
                        onClick={this.handleShareWith} />

                    <button onClick={this.handleSubmit} className="btn btn-primary">
                        {album ? translate(t, 'news.TR_UPDATE_LABEL') : translate(t, 'news.TR_CREATE_LABEL')}
                    </button>
                </div>
            </div>
        );
    }

    private handleShareWith = () => {
        this.setState({ showPublishAll: true });
    }

    private handleClose = () => {
        const { onClose } = this.props;
        this.resetState();
        onClose(undefined);
    };

    private renderMessageCard = () => {
        if (this.state.errorMessage) {
            return this.renderMessage(this.state.errorMessage, { color: 'red' });
        }
        return null;
    };
    
    private renderMessage = (message: string, style: {} = {}) => {
        return (
            message ? 
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="message">
                                <label style={style}>{message}</label>
                            </div>
                        </div>
                    </div>
                </div>
            : null
        );
    };

    public render() {
        const { show } = this.props;
        const { showPublishAll } = this.state;

        if (!show) return null;

        return (
            <div className="popupAlbums">
                <div className="popupAlbums-overlay" onClick={this.handleClose}></div>

                <div className="popupAlbums-container">
                    <button className="close-btn" onClick={this.handleClose}>
                        <i className="fas fa-times"></i>
                    </button>
                    <div className="popupAlbums-content">
                        {showPublishAll ? (
                            this.renderPublishAll()
                        ) : (
                            this.renderPublishAlbums()
                        )}

                    </div>
                    <PopUpRemove
                        show={this.state.showPopupRemove}
                        onClose={() => this.togglePopupRemove(-1, false)}
                        onSubmit={this.handleRemoveMedia}
                    />
                </div>
            </div>

        );
    }
}



export default withTranslation()(PopUpAlbums);